import MainLayout from '../../layouts/MainLayout';
import ProductGroupCard from '@components/ProductGroupCard';
import {getProductGroupList} from '@lib/product-group';
import {useRouter} from 'next/router';
import Breadcrumb from '@components/Common/Breadcrumb';

import styles from './style.module.css';
import {serverSideTranslations} from 'next-i18next/serverSideTranslations';
import {useTranslation} from 'next-i18next';
import HeadMeta from '@components/Header/HeadMeta';
import FullPageLoader from '../../components/FullPageLoader';
import {ProductGroupListResponseData} from '@interfaces/product-group-display';
import {LocaleEnum} from '@enums/locale';
import {GetStaticPropsContext} from 'next';
import {isValidURL} from '@lib/utils';
import {AxiosResponse} from 'axios';

export default function Products({data}: {data: ProductGroupListResponseData}) {
  const {t} = useTranslation(['product-display']);
  const router = useRouter();
  const locale = router.locale as LocaleEnum;

  const {product_groups, breadcrumb} = data ?? {content_container: {}};
  if (router.isFallback) {
    return <FullPageLoader />;
  }

  return (
    <MainLayout>
      <div className="mx-lg-5">
        <Breadcrumb
          borderBottom={true}
          classNames={`${styles.productGroupBreadcrumb}`}
          baseUrl={t('product_base_url')}
          justifyStart={true}
          list={breadcrumb}
        />
      </div>
      <HeadMeta
        title={
          data.content_container[`seo_page_title_${locale}`] ||
          data[`product_group_name_${locale}`]
        }
        description={data.content_container[`seo_description_${locale}`]}
        keywords={data.content_container[`seo_keywords_${locale}`]}
        canonical_url={data.content_container[`canonical_url_${locale}`]}
      />
      <div className="container">
        <div className="row mb-5">
          <h3
            className={'my-4'}
            title={data[`product_group_name_${locale}`]}
            style={{fontWeight: 600}}
          >
            {data[`product_group_name_${locale}`]}{' '}
          </h3>
          {product_groups.map(productGroup => {
            return (
              <div key={productGroup.id} className="col-sm-6 col-md-4 mb-4">
                <ProductGroupCard
                  name={productGroup[`product_group_name_${locale}`]}
                  description={
                    productGroup[`product_group_description_${locale}`]
                  }
                  seo_friendly_url={productGroup[`seo_friendly_url_${locale}`]}
                  image={productGroup.images || {}}
                  lowest_price={productGroup.lowest_price}
                  type={productGroup.type}
                  locale={locale}
                />
              </div>
            );
          })}
        </div>
      </div>
    </MainLayout>
  );
}

export async function getStaticProps({params, locale}: GetStaticPropsContext) {
  const slug = params!.slug as string;
  try {
    const response: AxiosResponse<{data: ProductGroupListResponseData}> =
      await getProductGroupList(slug, locale as LocaleEnum);
    const data = response.data.data;
    const redirectUrl =
      data[`product_group_redirect_url_${locale as LocaleEnum}`];
    if (redirectUrl && isValidURL(redirectUrl)) {
      return {
        redirect: {
          destination: redirectUrl,
          permanent: false,
        },
      };
    }
    return {
      props: {
        ...(await serverSideTranslations(locale!, [
          'common',
          'product-display',
        ])),
        data,
      },
    };
  } catch (error: any) {
    if (error.response.status < 500) {
      return {notFound: true};
    }
    throw error;
  }
}

export async function getStaticPaths() {
  return {
    paths: [],
    fallback: true,
  };
}
